import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [userSettings, setUserSettings] = useState(null) // ✅ NEW: Complete user settings (employee_id, position, etc.)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Load both user_profiles and user_settings in parallel
        const [profileResult, settingsResult] = await Promise.all([
          supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single(),
          supabase?.from('user_settings')?.select('*')?.eq('user_id', userId)?.single()
        ])
        
        if (!profileResult.error) setUserProfile(profileResult.data)
        if (!settingsResult.error) {
          setUserSettings(settingsResult.data) // ✅ NEW: Store user settings
          // ✅ Store employee_id in localStorage for API service
          if (settingsResult.data?.employee_id) {
            localStorage.setItem('employeeId', settingsResult.data.employee_id);
          }
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setUserSettings(null) // ✅ NEW: Clear settings on logout
      localStorage.removeItem('employeeId'); // ✅ Clear employee_id from storage
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // ✅ FIX: Track if auth has been initialized to prevent re-initialization on navigation
    const authInitialized = sessionStorage.getItem('winglet_auth_initialized');
    if (authInitialized && user) {
      // Already initialized and have a user - skip re-initialization
      return;
    }
    
    // Check for dev mode bypass (localStorage flag)
    const devModeEnabled = localStorage.getItem('winglet_dev_mode') === 'true';
    
    // Check if Supabase is configured (has URL)
    const supabaseConfigured = import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_URL !== 'undefined';
    
    if (!supabaseConfigured || devModeEnabled) {
      // Development mode - use mock authenticated user
      console.log('🔓 Development mode: Using mock authenticated user');
      
      // Check if dev user has completed onboarding
      const devOnboardingComplete = localStorage.getItem('winglet_dev_onboarding_complete') === 'true';
      
      const mockUser = {
        id: 'dragi-001',
        email: 'dragi@winglet.local',
        user_metadata: {
          employee_name: 'Dragi',
          employee_id: devOnboardingComplete ? 'dragi-001' : null,
          full_name: 'Dragi',
          role: 'admin'
        }
      };
      setUser(mockUser);
      setUserProfile({
        id: 'dragi-001',
        employee_name: devOnboardingComplete ? 'Dragi' : null,
        employee_id: devOnboardingComplete ? 'dragi-001' : null,  // Only set if onboarding complete
        full_name: 'Dragi',
        role: 'admin',
        position: devOnboardingComplete ? 'Captain' : null,
        home_base: devOnboardingComplete ? 'DFW' : null,
      });
      // ✅ NEW: Mock user settings for dev mode
      setUserSettings(devOnboardingComplete ? {
        user_id: 'dragi-001',
        employee_id: 'dragi-001',
        employee_name: 'Dragi',
        position: 'Captain',
        home_base: 'DFW',
        aircraft_type: '73N',
        monthly_guarantee: 75.0
      } : null);
      // ✅ Store dev employee_id
      if (devOnboardingComplete) {
        localStorage.setItem('employeeId', 'dragi-001');
      }
      setLoading(false);
      sessionStorage.setItem('winglet_auth_initialized', 'true'); // Mark as initialized
      return;
    }
    
    // Production mode - use real Supabase auth
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
      sessionStorage.setItem('winglet_auth_initialized', 'true'); // Mark as initialized
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, fullName = '', avatarUrl = '') => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            employee_name: fullName,
            avatar_url: avatarUrl,
            role: 'user'
          }
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location?.origin}/auth/callback`
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signInWithGithub = async () => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location?.origin}/auth/callback`
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signInWithAzure = async () => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: `${window.location?.origin}/auth/callback`,
          scopes: 'email'
        }
      })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      // Clear dev mode flag
      localStorage.removeItem('winglet_dev_mode');
      // ✅ Clear session cache in API service
      const { clearSessionCache } = await import('../services/apiService');
      clearSessionCache();
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    userSettings, // ✅ NEW: Complete user settings object
    loading,
    profileLoading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithGithub,
    signInWithAzure,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    needsOnboarding: !!user && !loading && !profileLoading && !userSettings?.employee_id, // ✅ FIXED: Check userSettings not userProfile
    employeeId: userSettings?.employee_id || userProfile?.employee_id || null, // ✅ FIXED: Get from userSettings first
    employeeName: userSettings?.employee_name || userProfile?.employee_name || null, // ✅ NEW: Employee name
    position: userSettings?.position || null, // ✅ NEW: Position (CA/FO)
    homeBase: userSettings?.home_base || null, // ✅ NEW: Home base
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}